import type { ContinuationRuntimeEstimate, RuntimeEstimateSource } from '../types';

type RuntimeEstimateInput = {
  workUnits: number;
  detail: string;
  source?: RuntimeEstimateSource;
};

const LOCAL_FIXED_MS = 12;
const LOCAL_MS_PER_WORK_UNIT = 0.003;
const BACKEND_FIXED_MS = 350;
const BACKEND_MS_PER_WORK_UNIT = 0.006;

const clampWorkUnits = (workUnits: number) => (
  Number.isFinite(workUnits) && workUnits > 0 ? Math.round(workUnits) : 0
);

export const formatRuntimeDuration = (durationMs: number): string => {
  if (!Number.isFinite(durationMs) || durationMs <= 0) {
    return '<50 ms';
  }

  if (durationMs < 50) {
    return '<50 ms';
  }

  if (durationMs < 1000) {
    const interval = durationMs < 200 ? 10 : 50;
    return `${Math.round(durationMs / interval) * interval} ms`;
  }

  if (durationMs < 10000) {
    return `${(Math.round(durationMs / 100) / 10).toFixed(1)} s`;
  }

  return `${Math.round(durationMs / 1000)} s`;
};

export const createRuntimeEstimate = ({
  workUnits,
  detail,
  source = 'heuristic'
}: RuntimeEstimateInput): ContinuationRuntimeEstimate => {
  const safeWorkUnits = clampWorkUnits(workUnits);
  const fixedMs = source === 'backend' ? BACKEND_FIXED_MS : LOCAL_FIXED_MS;
  const msPerWorkUnit = source === 'backend' ? BACKEND_MS_PER_WORK_UNIT : LOCAL_MS_PER_WORK_UNIT;
  const minimumMs = source === 'backend' ? 250 : 16;
  const estimatedMs = Math.max(minimumMs, fixedMs + safeWorkUnits * msPerWorkUnit);
  const shortLabel = formatRuntimeDuration(estimatedMs);

  return {
    estimatedMs,
    label: `est. ${shortLabel}`,
    shortLabel,
    detail,
    workUnits: safeWorkUnits,
    source,
    confidence: source === 'backend' ? 'low' : safeWorkUnits > 150000 ? 'medium' : 'high'
  };
};
